/**
 * Inline SVG icons used by the shell drawer. Inlined (not FontAwesome) so
 * `@cowbell/moo` does not pull a third-party icon set into the host bundle.
 *
 * All icons render at 1em and inherit color via `currentColor` — Mantine's
 * `c` prop or surrounding `color` styling controls them.
 */
import React from 'react';

interface IconProps {
  readonly size?: number | string;
  readonly className?: string;
  readonly 'aria-hidden'?: boolean;
}

function svgProps(size: number | string = '1em', className?: string) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };
}

export function HamburgerIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

export function PencilSquareIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M16 3l5 5-11 11H5v-5z" />
    </svg>
  );
}

export function CloseIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export function ArrowLeftIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <line x1="20" y1="12" x2="4" y2="12" />
      <polyline points="10 18 4 12 10 6" />
    </svg>
  );
}

export function ArrowUpRightFromSquareIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M14 4h6v6" />
      <line x1="20" y1="4" x2="10" y2="14" />
      <path d="M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
    </svg>
  );
}

export function TrashIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export function CheckIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function ChevronRightIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export function ThumbtackIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M9 4h6v5l3 3v3H6v-3l3-3z" />
      <line x1="12" y1="15" x2="12" y2="21" />
    </svg>
  );
}

export function ThumbtackFilledIcon({ size, className }: IconProps) {
  const props = svgProps(size, className);
  return (
    <svg {...props} fill="currentColor">
      <path d="M9 4h6v5l3 3v3H6v-3l3-3z" />
      <line x1="12" y1="15" x2="12" y2="21" stroke="currentColor" />
    </svg>
  );
}

export function SparkleIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M12 3l1.8 4.5L18 9l-4.2 1.5L12 15l-1.8-4.5L6 9l4.2-1.5z" />
      <path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z" />
    </svg>
  );
}

export function CopyIcon({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

